import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useReadingSession } from './ReadingSession';
import { captionWords } from './readingText';
import VoiceHalo from './VoiceHalo';
import './NarrationDock.css';

// Line icons drawn on a 24px grid in the same hairline weight as the
// engraved rules elsewhere; they take their colour from the button.
const Icon = ({ children }) => (
  <svg
    className="narration-dock__icon"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const BackIcon = () => (
  <Icon>
    <path d="M6.5 5.5v13" />
    <path d="M18 6.2v11.6a.6.6 0 0 1-.92.5L9 12.5a.6.6 0 0 1 0-1L17.08 5.7a.6.6 0 0 1 .92.5z" />
  </Icon>
);

const ForwardIcon = () => (
  <Icon>
    <path d="M17.5 5.5v13" />
    <path d="M6 6.2v11.6a.6.6 0 0 0 .92.5L15 12.5a.6.6 0 0 0 0-1L6.92 5.7a.6.6 0 0 0-.92.5z" />
  </Icon>
);

const PlayIcon = () => (
  <Icon>
    <path d="M8.5 6.3v11.4a.7.7 0 0 0 1.06.6l9.1-5.7a.7.7 0 0 0 0-1.2l-9.1-5.7a.7.7 0 0 0-1.06.6z" fill="currentColor" />
  </Icon>
);

const PauseIcon = () => (
  <Icon>
    <rect x="7" y="5.5" width="3" height="13" rx="0.8" fill="currentColor" />
    <rect x="14" y="5.5" width="3" height="13" rx="0.8" fill="currentColor" />
  </Icon>
);

const ReplayIcon = () => (
  <Icon>
    <path d="M5.5 12a6.5 6.5 0 1 0 1.9-4.6" />
    <path d="M7 3.8v3.9h3.9" />
  </Icon>
);

const SpeakerIcon = ({ muted }) => (
  <Icon>
    <path d="M4.5 9.5h3l4.5-4v13l-4.5-4h-3a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5z" />
    {muted ? (
      <path d="M16 9.5l5 5M21 9.5l-5 5" />
    ) : (
      <>
        <path d="M15.5 9.2a4 4 0 0 1 0 5.6" />
        <path d="M18.2 6.6a7.6 7.6 0 0 1 0 10.8" />
      </>
    )}
  </Icon>
);

const TextIcon = () => (
  <Icon>
    <path d="M5 6.5h14M5 10.5h14M5 14.5h8" />
    <path d="M16.5 15.5l2.5 2.5 2.5-2.5" />
  </Icon>
);

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Whether the sentence being spoken can be seen in the full reading above
 * the dock; the captions would only repeat it then.
 */
const spokenTextInView = (root, dockHeight) => {
  const now = root?.querySelector('.reading-text__now');
  if (!now) return false;
  const rect = now.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight - dockHeight;
};

/**
 * The narration dock, pinned to the bottom of the screen: see-through
 * controls (back / play-pause / forward, mute and volume, a hairline of
 * progress) with the words being spoken as captions beneath them. The page
 * no longer follows the text; the full reading waits further down for
 * anyone who scrolls to it, and the captions step aside while it is in view.
 *
 * Rendered into <body>: the spread sits in a transformed slider, inside
 * which `position: fixed` would pin to the slider instead of the screen.
 * A spacer stays in the page so the dock never covers its last lines.
 */
function NarrationDock() {
  const { t } = useTranslation();
  const session = useReadingSession();
  const dockRef = useRef(null);
  const [dockHeight, setDockHeight] = useState(0);
  const [textInView, setTextInView] = useState(false);

  const shown = Boolean(session?.active && session.narrated);
  const narrator = session?.narrator;
  const spokenIndex = narrator ? narrator.cursor.spoken - 1 : -1;
  const readingRef = session?.readingRef;

  useLayoutEffect(() => {
    const dock = dockRef.current;
    if (!shown || !dock) return undefined;
    const measure = () => setDockHeight(dock.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(dock);
    return () => observer.disconnect();
  }, [shown]);

  useEffect(() => {
    if (!shown) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      setTextInView(spokenTextInView(readingRef.current, dockHeight));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [shown, readingRef, dockHeight, spokenIndex]);

  if (!shown) return null;

  const { sentences, status, haloVisible } = session;
  const {
    cursor, paused, blocked, ended, speaking, volume, muted, analyser
  } = narrator;
  const total = sentences.length;
  const finished = status === 'done';

  // Ended for good only once the stream is done; before that the voice is
  // just waiting for the next sentence.
  const done = ended && finished;
  const stopped = paused || blocked || done;
  const silent = muted || volume === 0;

  const onPlay = () => {
    if (paused || blocked) narrator.resume();
    else if (done) narrator.seekToSentence(0);
    else narrator.pause();
  };

  const onMute = () => {
    // Unmuting at zero volume would change nothing audible
    if (silent && volume === 0) narrator.setVolume(0.7);
    if (muted || !silent) narrator.toggleMute();
  };

  const onVolume = (event) => {
    const value = Number(event.target.value);
    narrator.setVolume(value);
    if (muted && value > 0) narrator.toggleMute();
  };

  const showFullText = () => {
    session.readingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const playLabel = done ? t('voice.replay') : stopped ? t('voice.play') : t('voice.pause');
  const current = Math.max(1, cursor.spoken);
  const progress = done ? 1 : total ? clamp01((Math.max(0, cursor.spoken - 1) + cursor.words) / total) : 0;
  const level = silent ? 0 : volume;

  // The caption: the sentence the voice is on, word by word as it is said
  let caption = null;
  if (status === 'error') {
    caption = (
      <span className="narration-dock__note">
        {t('reading.error')}{' '}
        <button type="button" className="narration-dock__link" onClick={session.retry}>
          {t('reading.retry')}
        </button>
      </span>
    );
  } else if (spokenIndex >= 0 && spokenIndex < total) {
    const words = captionWords(sentences[spokenIndex], prefersReducedMotion() ? 1 : cursor.words);
    caption = words.map((word, i) => (
      // Keyed by sentence, so each word fades in once as it is spoken
      <span key={`${spokenIndex}:${i}`} className="narration-dock__word">{word} </span>
    ));
  } else if (!done) {
    caption = <span className="narration-dock__note">{t('reading.loading')}</span>;
  }

  const captionsHidden = textInView || done || !caption;

  const dock = (
    <section
      ref={dockRef}
      className="narration-dock"
      aria-label={t('voice.player')}
      style={{ '--progress': progress }}
    >
      <div className="narration-dock__panel">
        <div
          className="narration-dock__progress"
          role="progressbar"
          aria-label={t('voice.progress')}
          aria-valuemin={0}
          aria-valuemax={Math.max(total, 1)}
          aria-valuenow={Math.min(cursor.spoken, total)}
          aria-valuetext={t('voice.position', { current: Math.min(current, Math.max(total, 1)), total })}
        >
          <span className="narration-dock__progress-fill" />
          <span className="narration-dock__progress-head" />
        </div>

        <div className="narration-dock__row">
          <div className="narration-dock__lead">
            {/* The big circle is above the cards; this one stands in for it
                once that has scrolled out of sight */}
            {!haloVisible && (
              <VoiceHalo analyser={analyser} active={!done} playing={speaking} size="small" />
            )}
            {/* Visual counter only; the progressbar above announces the same */}
            <span className="narration-dock__count" aria-hidden="true">
              {total > 0 && `${Math.min(current, total)} / ${total}`}
            </span>
          </div>

          <div className="narration-dock__transport">
            <button
              type="button"
              className="narration-dock__btn"
              onClick={narrator.back}
              disabled={!narrator.canBack}
              aria-label={t('voice.back')}
              title={t('voice.back')}
            >
              <BackIcon />
            </button>

            <button
              type="button"
              className={`narration-dock__btn narration-dock__btn--play${blocked ? ' is-calling' : ''}`}
              onClick={onPlay}
              aria-label={blocked ? t('voice.tapToListen') : playLabel}
              title={blocked ? t('voice.tapToListen') : playLabel}
            >
              {done ? <ReplayIcon /> : stopped ? <PlayIcon /> : <PauseIcon />}
            </button>

            <button
              type="button"
              className="narration-dock__btn"
              onClick={narrator.forward}
              disabled={!narrator.canForward}
              aria-label={t('voice.forward')}
              title={t('voice.forward')}
            >
              <ForwardIcon />
            </button>
          </div>

          <div className="narration-dock__end">
            <button
              type="button"
              className="narration-dock__btn"
              onClick={onMute}
              aria-pressed={silent}
              aria-label={t('voice.mute')}
              title={t('voice.mute')}
            >
              <SpeakerIcon muted={silent} />
            </button>

            <input
              type="range"
              className="narration-dock__slider"
              min="0"
              max="1"
              step="0.05"
              value={level}
              onChange={onVolume}
              aria-label={t('voice.volume')}
              aria-valuetext={`${Math.round(level * 100)}%`}
              style={{ '--level': level }}
            />

            <button
              type="button"
              className="narration-dock__btn"
              onClick={showFullText}
              aria-label={t('voice.fullText')}
              title={t('voice.fullText')}
            >
              <TextIcon />
            </button>
          </div>
        </div>
      </div>

      {/* The voice already says these words and the full text stays
          readable below, so screen readers skip the captions */}
      <p
        className={`narration-dock__captions${captionsHidden ? ' is-hidden' : ''}`}
        aria-hidden="true"
      >
        <span className="narration-dock__caption">{caption}</span>
      </p>
    </section>
  );

  return (
    <>
      <div className="narration-dock-spacer" style={{ height: dockHeight }} aria-hidden="true" />
      {createPortal(dock, document.body)}
    </>
  );
}

export default NarrationDock;
